import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CheckCircle2, Download, XCircle } from "lucide-react";
import { generateLnwPdf, generateLnwPdfBase64, LnwPdfData } from "@/lib/lnwPdf";
import { format } from "date-fns";
import lnwLogo from "@/assets/lnw-template-logo.png.asset.json";

interface PublicLnw {
  lnwNumber: string;
  issueDate: string;
  status: "draft" | "sent" | "approved" | "rejected";
  notes: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  rejectionComment: string | null;
  archived?: boolean;
  project: {
    name: string;
    order_reference: string | null;
    customers: { company: string; contact_name: string | null; address: string | null };
  } | null;
  order: { name: string; order_number: string | null } | null;
  items: { position: number; work_date: string; hours: number; description: string }[];
}

const fmtDate = (iso: string) => format(new Date(iso), "dd.MM.yyyy");
const fmtHours = (h: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(h);

const LnwPublic = () => {
  const { token } = useParams<{ token: string }>();
  const [lnw, setLnw] = useState<PublicLnw | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approverEmail, setApproverEmail] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data, error: fnError } = await supabase.functions.invoke("lnw-public-get", {
        body: { token },
      });
      if (fnError) {
        const details = fnError instanceof FunctionsHttpError ? await fnError.context.text() : fnError.message;
        try {
          setError(JSON.parse(details)?.error ?? "Der Leistungsnachweis konnte nicht geladen werden.");
        } catch {
          setError("Der Leistungsnachweis konnte nicht geladen werden.");
        }
      } else {
        setLnw(data as PublicLnw);
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const act = async (action: "approve" | "reject") => {
    setBusy(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("lnw-approve", {
        body: { token, action, name, comment, approverEmail: approverEmail.trim(), siteUrl: window.location.origin },
      });
      if (fnError) {
        const details = fnError instanceof FunctionsHttpError ? await fnError.context.text() : fnError.message;
        let msg = "Aktion fehlgeschlagen";
        try {
          msg = JSON.parse(details)?.error ?? msg;
        } catch { /* keep default */ }
        toast.error(msg);
        return;
      }
      const approvedNow = action === "approve";
      const updated: PublicLnw | null = lnw
        ? {
            ...lnw,
            status: data.status,
            approvedAt: approvedNow ? new Date().toISOString() : lnw.approvedAt,
            approvedByName: approvedNow ? name : lnw.approvedByName,
            rejectionComment: action === "reject" ? comment : lnw.rejectionComment,
          }
        : null;
      setLnw(updated);
      toast.success(approvedNow ? "Vielen Dank, der Leistungsnachweis wurde genehmigt." : "Der Leistungsnachweis wurde abgelehnt.");

      // Das genehmigte Dokument einmalig unveränderlich archivieren.
      if (approvedNow && updated) {
        try {
          const pdf = await generateLnwPdfBase64(pdfData(updated));
          await supabase.functions.invoke("lnw-archive-pdf", { body: { token, pdf } });
          setLnw((prev) => (prev ? { ...prev, archived: true } : prev));
        } catch (err) {
          console.error("Archivierung fehlgeschlagen", err);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const pdfData = (source: PublicLnw): LnwPdfData => ({
    lnwNumber: source.lnwNumber,
    issueDate: source.issueDate,
    customerCompany: source.project?.customers?.company ?? "",
    projectName: source.project?.name ?? "",
    orderReference: source.order?.order_number ?? source.project?.order_reference ?? null,
    status: source.status,
    approvedAt: source.approvedAt,
    approvedByName: source.approvedByName,
    items: source.items.map((item) => ({
      position: item.position,
      work_date: item.work_date,
      hours: Number(item.hours),
      description: item.description,
    })),
  });

  const downloadPdf = async () => {
    if (!lnw) return;
    setPdfBusy(true);
    try {
      // Bevorzugt das archivierte Originaldokument ausliefern.
      const { data, error: archiveError } = await supabase.functions.invoke("lnw-public-pdf", { body: { token } });
      if (!archiveError && data?.url) {
        window.open(data.url, "_blank", "noopener");
        return;
      }
      await generateLnwPdf(pdfData(lnw));
    } catch (err) {
      console.error(err);
      toast.error("Das PDF konnte nicht erstellt werden.");
    } finally {
      setPdfBusy(false);
    }
  };

  const totalHours = lnw?.items.reduce((acc, i) => acc + Number(i.hours), 0) ?? 0;

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4">
      <Helmet>
        <title>Leistungsnachweis | FACHSPRECHPARTNER</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-lg font-bold tracking-wide">FACHSPRECHPARTNER</p>
            <p className="text-sm italic text-muted-foreground">IT/OT für GA, FM und Gebäudebetreiber</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Tim Bauer · <a href="mailto:info@fachsprechpartner.de" className="underline">info@fachsprechpartner.de</a>
            </p>
          </div>
          <img
            src={lnwLogo.url}
            alt="FACHSPRECHPARTNER Logo"
            className="h-16 w-16 shrink-0 object-contain sm:h-20 sm:w-20"
          />
        </header>

        {!loading && !error && lnw && (
          <ol className="mb-6 flex items-center gap-2 text-xs sm:text-sm" aria-label="Fortschritt">
            {[
              { label: "Prüfen", done: true },
              { label: "Freigeben", done: lnw.status === "approved" || lnw.status === "rejected" },
              { label: "Bestätigung", done: lnw.status === "approved" },
            ].map((step, index) => (
              <li key={step.label} className="flex flex-1 items-center gap-2">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${
                    step.done ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground"
                  }`}
                >
                  {index + 1}
                </span>
                <span className={step.done ? "font-medium" : "text-muted-foreground"}>{step.label}</span>
                {index < 2 && <span className="hidden h-px flex-1 bg-border sm:block" />}
              </li>
            ))}
          </ol>
        )}

        {loading ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">Leistungsnachweis wird geladen…</CardContent></Card>
        ) : error || !lnw ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">{error ?? "Nicht gefunden."}</CardContent></Card>
        ) : (
          <>
            <Card className="mb-6">
              <CardContent className="pt-8">
                <h1 className="mb-8 text-2xl font-bold">Leistungsnachweis</h1>
                <dl className="mb-8 space-y-2 text-sm sm:text-base">
                  <div className="flex justify-between gap-4 border-b pb-2">
                    <dt className="text-muted-foreground">Auftraggeber</dt>
                    <dd className="text-right font-medium">{lnw.project?.customers?.company}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b pb-2">
                    <dt className="text-muted-foreground">Auftragskennung</dt>
                    <dd className="text-right font-medium">{lnw.order?.order_number ?? lnw.project?.order_reference ?? "–"}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b pb-2">
                    <dt className="text-muted-foreground">LNW-Kennung</dt>
                    <dd className="text-right font-medium">{lnw.lnwNumber}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b pb-2">
                    <dt className="text-muted-foreground">Datum</dt>
                    <dd className="text-right font-medium">{fmtDate(lnw.issueDate)}</dd>
                  </div>
                </dl>

                <div className="hidden sm:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Pos.</TableHead>
                        <TableHead>Datum</TableHead>
                        <TableHead className="text-right">Aufwand (Std.)</TableHead>
                        <TableHead>Beschreibung</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lnw.items.map((item) => (
                        <TableRow key={item.position}>
                          <TableCell>{item.position}</TableCell>
                          <TableCell>{fmtDate(item.work_date)}</TableCell>
                          <TableCell className="text-right">{fmtHours(Number(item.hours))}</TableCell>
                          <TableCell>{item.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <ul className="space-y-3 sm:hidden">
                  {lnw.items.map((item) => (
                    <li key={item.position} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Pos. {item.position} · {fmtDate(item.work_date)}</span>
                        <span className="font-semibold text-foreground">{fmtHours(Number(item.hours))} h</span>
                      </div>
                      <p className="mt-1 text-sm">{item.description}</p>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-center justify-between border-y py-3 text-lg font-bold">
                  <span>Gesamtaufwand</span>
                  <span>{fmtHours(totalHours)} h</span>
                </div>

                <p className="mt-6 text-sm sm:text-base">Die Leistungen wurden wie oben beschrieben erbracht.</p>
              </CardContent>
            </Card>

            {lnw.status === "approved" && (
              <Card className="border-success/40 bg-success/5">
                <CardContent className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                    <div>
                      <p className="font-semibold">Genehmigt</p>
                      <p className="text-sm text-muted-foreground">
                        Online genehmigt am {lnw.approvedAt ? format(new Date(lnw.approvedAt), "dd.MM.yyyy 'um' HH:mm") : "–"}
                        {lnw.approvedByName ? ` durch ${lnw.approvedByName}` : ""}.
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" className="shrink-0 gap-2" onClick={downloadPdf} disabled={pdfBusy}>
                    <Download className="h-4 w-4" />
                    {pdfBusy ? "PDF wird erstellt…" : "PDF herunterladen"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {lnw.status === "rejected" && (
              <Card className="border-destructive/40 bg-destructive/5">
                <CardContent className="flex items-start gap-3 py-5">
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                  <div>
                    <p className="font-semibold">Abgelehnt</p>
                    <p className="text-sm text-muted-foreground">
                      Sie haben diesen Leistungsnachweis abgelehnt{lnw.rejectionComment ? `: ${lnw.rejectionComment}` : "."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {lnw.status === "sent" && (
              <Card>
                <CardContent className="space-y-4 pt-6">
                  {!rejecting ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="approver-name">Ihr Name (zur Bestätigung) *</Label>
                        <Input
                          id="approver-name"
                          placeholder="Vor- und Nachname"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="approver-email">Ihre E-Mail-Adresse (für die Bestätigung)</Label>
                        <Input
                          id="approver-email"
                          type="email"
                          placeholder="name@unternehmen.de"
                          value={approverEmail}
                          onChange={(e) => setApproverEmail(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button className="flex-1" disabled={busy || name.trim().length < 2} onClick={() => act("approve")}>
                          {busy ? "Bitte warten…" : "Leistungsnachweis genehmigen"}
                        </Button>
                        <Button variant="outline" className="flex-1" disabled={busy} onClick={() => setRejecting(true)}>
                          Ablehnen
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Mit der Genehmigung bestätigen Sie, dass die aufgeführten Leistungen wie beschrieben erbracht wurden.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="reject-comment">Grund der Ablehnung *</Label>
                        <Textarea
                          id="reject-comment"
                          rows={3}
                          placeholder="Bitte beschreiben Sie kurz, was angepasst werden soll."
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button variant="destructive" className="flex-1" disabled={busy || comment.trim().length < 3} onClick={() => act("reject")}>
                          {busy ? "Bitte warten…" : "Ablehnung absenden"}
                        </Button>
                        <Button variant="outline" className="flex-1" disabled={busy} onClick={() => setRejecting(false)}>
                          Zurück
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        <footer className="mt-8 text-center text-xs text-muted-foreground">
          FACHSPRECHPARTNER · info@fachsprechpartner.de
        </footer>
      </div>
    </div>
  );
};

export default LnwPublic;
